import React, { useRef, useEffect, useState, useCallback } from 'react';

export function RecordingPlayer({ recording, streamUrl, onClose, onDownload }) {
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSeekable, setIsSeekable] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const controlsTimeoutRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleDurationChange = () => {
      const dur = video.duration;
      if (dur && !isNaN(dur) && isFinite(dur)) {
        setDuration(dur);
        console.log(`Recording duration loaded: ${dur}s`);
      }
    };

    const handleLoadedMetadata = () => {
      const dur = video.duration;
      if (dur && !isNaN(dur) && isFinite(dur)) {
        setDuration(dur);
        setIsSeekable(true);
        setIsLoading(false);
        console.log(`Recording metadata loaded: duration=${dur}s, seekable=${video.seekable.length > 0}`);
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(null);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    const handleError = (e) => {
      console.error('Recording playback error:', e);
      setError('Failed to load video');
      setIsLoading(false);
    };

    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };

    const handleSeeking = () => setIsSeeking(true);
    const handleSeeked = () => {
      setIsSeeking(false);
      setCurrentTime(video.currentTime);
    };

    const handleProgress = () => {
      // Check if video is seekable
      if (video.seekable.length > 0) {
        setIsSeekable(true);
      }
      // Update buffered progress
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        setBuffered(bufferedEnd);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('progress', handleProgress);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('progress', handleProgress);
    };
  }, [isSeeking]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(err => {
        console.error('Play failed:', err);
        setError('Cannot play video');
      });
    }
  };

  const handleSeek = useCallback((e) => {
    const video = videoRef.current;
    const progressBar = progressBarRef.current;
    if (!video || !progressBar) return;

    // Use duration from state or video element
    const videoDuration = duration || video.duration;
    if (!videoDuration || isNaN(videoDuration) || !isFinite(videoDuration)) {
      console.warn('Cannot seek: duration not available');
      return;
    }

    const rect = progressBar.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const seekTime = percentage * videoDuration;

    console.log(`Seeking to ${seekTime.toFixed(2)}s (${(percentage * 100).toFixed(1)}%)`);

    setIsSeeking(true);
    setCurrentTime(seekTime);
    video.currentTime = seekTime;
  }, [duration]);

  // Skip forward/backward buttons
  const skipTime = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) return;

    const videoDuration = duration || video.duration;
    if (!videoDuration || isNaN(videoDuration)) return;

    const newTime = Math.max(0, Math.min(video.currentTime + seconds, videoDuration));
    video.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsMuted(!isMuted);
    video.muted = !isMuted;
  }, [isMuted]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen?.() || container.webkitRequestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.() || document.webkitExitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Handle progress bar hover for time preview
  const handleProgressHover = useCallback((e) => {
    const progressBar = progressBarRef.current;
    if (!progressBar || !duration) return;

    const rect = progressBar.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    setHoverTime(percentage * duration);
    setHoverPosition(x);
  }, [duration]);

  const handleProgressLeave = useCallback(() => {
    setHoverTime(null);
  }, []);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipTime(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipTime(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(v => Math.min(1, v + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(v => Math.max(0, v - 0.1));
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Escape':
          if (isFullscreen) {
            document.exitFullscreen?.();
          } else {
            onClose();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, skipTime, toggleMute, toggleFullscreen, isFullscreen, onClose]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const metadata = (() => {
    const raw = recording?.metadata;
    if (!raw) return {};
    if (typeof raw !== 'string') return raw;
    try { return JSON.parse(raw) || {}; } catch { return {}; }
  })();

  return (
    <div className="recording-player-overlay" onClick={onClose}>
      <div
        ref={containerRef}
        className={`recording-player-modal ${isFullscreen ? 'fullscreen' : ''}`}
        onClick={e => e.stopPropagation()}
        onMouseMove={resetControlsTimeout}
      >
        {/* Header */}
        <div className={`player-header ${!showControls && isPlaying ? 'hidden' : ''}`}>
          <div className="player-title">
            <div className="title-badge">
              <span className="badge-dot"></span>
              REC
            </div>
            <div className="title-text">
              <h2>{metadata.unitCode || recording?.unit_id?.substring(0, 8) || 'Recording'}</h2>
              <span className="recording-date">{formatDate(recording?.start_time)}</span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose} title="Close (Esc)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Video */}
        <div className="player-video-container">
          <video
            ref={videoRef}
            src={streamUrl}
            className="player-video"
            onClick={togglePlay}
            preload="metadata"
            playsInline
          />

          {/* Loading Overlay */}
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <span>Loading...</span>
            </div>
          )}

          {/* Error Overlay */}
          {error && (
            <div className="error-overlay">
              <span>{error}</span>
            </div>
          )}

          {/* Play/Pause Overlay */}
          {!isPlaying && !isLoading && !error && (
            <div className="play-overlay" onClick={togglePlay}>
              <div className="play-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
          )}

          {/* Seeking indicator */}
          {isSeeking && (
            <div className="seeking-indicator">
              <span>Seeking...</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className={`player-controls ${!showControls && isPlaying ? 'hidden' : ''}`}>
          {/* Progress Bar */}
          <div className="progress-container">
            <div
              ref={progressBarRef}
              className={`progress-bar ${isSeekable ? 'seekable' : 'not-seekable'}`}
              onClick={handleSeek}
              onMouseMove={handleProgressHover}
              onMouseLeave={handleProgressLeave}
            >
              {/* Buffered progress */}
              <div
                className="progress-buffered"
                style={{ width: `${duration > 0 ? (buffered / duration) * 100 : 0}%` }}
              />
              {/* Current progress */}
              <div
                className="progress-fill"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
              {/* Seek handle */}
              {duration > 0 && (
                <div
                  className="progress-handle"
                  style={{ left: `${(currentTime / duration) * 100}%` }}
                />
              )}
              {/* Hover time tooltip */}
              {hoverTime !== null && (
                <div className="progress-tooltip" style={{ left: `${hoverPosition}px` }}>
                  {formatTime(hoverTime)}
                </div>
              )}
            </div>
          </div>

          <div className="controls-row">
            {/* Left Controls */}
            <div className="controls-left">
              <button className="control-btn icon-btn skip-btn" onClick={() => skipTime(-10)} title="Skip back 10s">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.5 3C17.15 3 21.08 6.03 22.47 10.22L20.1 11C19.05 7.81 16.04 5.5 12.5 5.5C10.54 5.5 8.77 6.22 7.38 7.38L10 10H3V3L5.6 5.6C7.45 4 9.85 3 12.5 3M10 12L8.5 13.5L14.5 19.5L16 18L10 12M2 21.5V20H10.5V21.5H2Z"/>
                </svg>
                <span>10</span>
              </button>

              <button className="control-btn play-btn" onClick={togglePlay} disabled={isLoading || !!error}>
                {isPlaying ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>

              <button className="control-btn icon-btn skip-btn" onClick={() => skipTime(10)} title="Skip forward 10s">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.5 3C6.85 3 2.92 6.03 1.53 10.22L3.9 11C4.95 7.81 7.96 5.5 11.5 5.5C13.46 5.5 15.23 6.22 16.62 7.38L14 10H21V3L18.4 5.6C16.55 4 14.15 3 11.5 3M14 12L15.5 13.5L9.5 19.5L8 18L14 12M22 21.5V20H13.5V21.5H22Z"/>
                </svg>
                <span>10</span>
              </button>

              <div className="time-display">
                <span className="time-current">{formatTime(currentTime)}</span>
                <span className="time-separator">/</span>
                <span className="time-total">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Right Controls */}
            <div className="controls-right">
              {/* Volume */}
              <div className="volume-control">
                <button className="control-btn icon-btn mute-btn" onClick={toggleMute} title={isMuted ? 'Unmute (M)' : 'Mute (M)'}>
                  {isMuted || volume === 0 ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    setVolume(val);
                    if (val > 0 && isMuted) setIsMuted(false);
                  }}
                  className="volume-slider"
                />
              </div>

              {/* Playback Speed */}
              <select
                value={playbackRate}
                onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                className="speed-select"
                title="Playback speed"
              >
                <option value="0.5">0.5x</option>
                <option value="0.75">0.75x</option>
                <option value="1">1x</option>
                <option value="1.25">1.25x</option>
                <option value="1.5">1.5x</option>
                <option value="2">2x</option>
              </select>

              {/* Fullscreen */}
              <button className="control-btn icon-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}>
                {isFullscreen ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                  </svg>
                )}
              </button>

              {/* Download */}
              <button
                className="control-btn download-btn"
                onClick={() => onDownload(recording.id)}
                title="Download video"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/>
                </svg>
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="player-metadata">
          <div className="metadata-grid">
            {metadata.responderName && (
              <div className="metadata-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="meta-icon">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span className="meta-value">{metadata.responderName}</span>
              </div>
            )}
            {metadata.gps && (
              <div className="metadata-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="meta-icon">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span className="meta-value">
                  {metadata.gps.lat?.toFixed(4)}, {metadata.gps.lng?.toFixed(4)}
                </span>
              </div>
            )}
            <div className="metadata-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="meta-icon">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span className="meta-value">{formatTime(recording?.duration_seconds)}</span>
            </div>
            <div className="metadata-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="meta-icon">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                <polyline points="13 2 13 9 20 9"/>
              </svg>
              <span className="meta-value">
                {((recording?.file_size_bytes || 0) / 1024 / 1024).toFixed(1)} MB
              </span>
            </div>
          </div>
        </div>
      </div>

      <RecordingPlayerStyles />
    </div>
  );
}

const RecordingPlayerStyles = () => (
  <style>{`
    /* Premium Video Player Styles */
    .recording-player-overlay {
      position: fixed;
      inset: 0;
      background: radial-gradient(ellipse at center, rgba(15, 15, 20, 0.95) 0%, rgba(5, 5, 10, 0.98) 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      box-sizing: border-box;
      backdrop-filter: blur(30px) saturate(180%);
      animation: overlayFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes overlayFadeIn {
      from { opacity: 0; backdrop-filter: blur(0); }
      to { opacity: 1; backdrop-filter: blur(30px) saturate(180%); }
    }

    .recording-player-modal {
      background: linear-gradient(165deg, rgba(30, 30, 40, 0.98) 0%, rgba(18, 18, 25, 0.99) 100%);
      border-radius: 20px;
      width: 100%;
      max-width: 960px;
      max-height: calc(100vh - 40px);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow:
        0 0 0 1px rgba(255, 255, 255, 0.06),
        0 0 80px rgba(99, 102, 241, 0.15),
        0 25px 50px -12px rgba(0, 0, 0, 0.5);
      animation: modalSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .recording-player-modal.fullscreen {
      max-width: 100%;
      max-height: 100%;
      border-radius: 0;
      height: 100vh;
    }

    @keyframes modalSlideIn {
      from { opacity: 0; transform: translateY(40px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    /* Header */
    .player-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px;
      background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 100%);
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      flex-shrink: 0;
      transition: opacity 0.3s, transform 0.3s;
    }

    .player-header.hidden {
      opacity: 0;
      transform: translateY(-100%);
      pointer-events: none;
    }

    .player-title {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .title-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
      color: white;
      padding: 7px 12px;
      border-radius: 8px;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 1.2px;
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35), inset 0 1px 0 rgba(255,255,255,0.2);
    }

    .badge-dot {
      width: 6px;
      height: 6px;
      background: #fff;
      border-radius: 50%;
      animation: badgePulse 1.5s ease-in-out infinite;
    }

    @keyframes badgePulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.8); }
    }

    .title-text { display: flex; flex-direction: column; gap: 3px; }
    .player-title h2 {
      margin: 0;
      font-size: 17px;
      font-weight: 600;
      color: #fff;
      letter-spacing: -0.4px;
    }
    .recording-date {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.45);
      font-weight: 500;
    }

    .close-btn {
      width: 42px;
      height: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .close-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
      transform: scale(1.05);
      border-color: rgba(255, 255, 255, 0.15);
    }
    .close-btn:active { transform: scale(0.95); }

    /* Video Container */
    .player-video-container {
      position: relative;
      background: #000;
      flex: 1;
      min-height: 200px;
      max-height: 65vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .fullscreen .player-video-container { max-height: none; flex: 1; }

    .player-video {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }

    /* Play Overlay */
    .play-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: radial-gradient(circle at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%);
      cursor: pointer;
      transition: all 0.35s ease;
    }
    .play-overlay:hover { background: radial-gradient(circle at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%); }
    .play-overlay:hover .play-icon {
      transform: scale(1.1);
      box-shadow: 0 0 60px rgba(255, 255, 255, 0.3), 0 10px 40px rgba(0, 0, 0, 0.3);
    }

    .play-icon {
      width: 88px;
      height: 88px;
      background: linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(240,240,240,0.95) 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1a1a1a;
      box-shadow: 0 0 40px rgba(255, 255, 255, 0.2), 0 10px 30px rgba(0, 0, 0, 0.25);
      transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .play-icon svg { margin-left: 5px; }

    /* Controls */
    .player-controls {
      padding: 16px 24px 14px;
      background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.15) 100%);
      flex-shrink: 0;
      transition: opacity 0.3s, transform 0.3s;
    }
    .player-controls.hidden {
      opacity: 0;
      transform: translateY(100%);
      pointer-events: none;
    }

    /* Progress Bar */
    .progress-container { margin-bottom: 14px; position: relative; }

    .progress-bar {
      height: 5px;
      background: rgba(255, 255, 255, 0.12);
      border-radius: 10px;
      cursor: pointer;
      position: relative;
      transition: height 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      overflow: visible;
    }
    .progress-bar.not-seekable { cursor: not-allowed; opacity: 0.4; }
    .progress-bar:hover { height: 8px; }
    .progress-bar:hover .progress-handle {
      transform: translateX(-50%) translateY(-50%) scale(1);
      opacity: 1;
    }
    .progress-bar:hover .progress-tooltip { opacity: 1; transform: translateX(-50%) translateY(0); }

    .progress-buffered {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 10px;
      pointer-events: none;
    }

    .progress-fill {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
      border-radius: 10px;
      pointer-events: none;
      box-shadow: 0 0 20px rgba(139, 92, 246, 0.5), 0 0 40px rgba(139, 92, 246, 0.2);
    }

    .progress-handle {
      position: absolute;
      top: 50%;
      width: 16px;
      height: 16px;
      background: linear-gradient(145deg, #fff 0%, #e8e8e8 100%);
      border: 2px solid #8b5cf6;
      border-radius: 50%;
      transform: translateX(-50%) translateY(-50%) scale(0);
      opacity: 0;
      transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      pointer-events: none;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(139, 92, 246, 0.2);
      z-index: 10;
    }

    .progress-tooltip {
      position: absolute;
      bottom: 100%;
      margin-bottom: 12px;
      padding: 6px 10px;
      background: rgba(20, 20, 25, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      font-family: 'SF Mono', Monaco, monospace;
      border-radius: 8px;
      transform: translateX(-50%) translateY(8px);
      opacity: 0;
      transition: all 0.2s ease;
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 20;
    }

    /* Overlays */
    .loading-overlay, .error-overlay, .seeking-indicator {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(12px);
      color: white;
      font-size: 14px;
      gap: 18px;
      font-weight: 500;
    }
    .error-overlay {
      background: linear-gradient(180deg, rgba(185, 28, 28, 0.9) 0%, rgba(127, 29, 29, 0.95) 100%);
    }
    .seeking-indicator { background: rgba(0, 0, 0, 0.7); }
    .seeking-indicator span {
      padding: 10px 20px;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 100px;
      font-size: 13px;
    }

    .loading-spinner {
      width: 52px;
      height: 52px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top-color: #8b5cf6;
      border-radius: 50%;
      animation: spinnerRotate 0.8s linear infinite;
    }
    @keyframes spinnerRotate { to { transform: rotate(360deg); } }

    /* Controls Row */
    .controls-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
    }
    .controls-left, .controls-right {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    /* Buttons */
    .control-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 14px;
      background: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.85);
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: 10px;
      cursor: pointer;
      font-weight: 600;
      font-size: 13px;
      transition: all 0.2s ease;
    }
    .control-btn:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
    .control-btn:active:not(:disabled) { transform: scale(0.95); }
    .control-btn:disabled { opacity: 0.35; cursor: not-allowed; }

    .control-btn.icon-btn {
      flex-direction: column;
      gap: 2px;
      padding: 8px 14px;
      background: transparent;
      border-color: transparent;
    }
    .control-btn.icon-btn span { font-size: 10px; font-weight: 700; opacity: 0.65; }
    .control-btn.icon-btn:hover { background: rgba(255, 255, 255, 0.06); }

    .control-btn.mute-btn {
      padding: 8px;
      background: transparent;
      border-color: transparent;
    }

    .control-btn.play-btn {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(145deg, #7c3aed 0%, #6366f1 100%);
      border: none;
      box-shadow: 0 3px 16px rgba(99, 102, 241, 0.35);
    }
    .control-btn.play-btn:hover:not(:disabled) {
      background: linear-gradient(145deg, #8b5cf6 0%, #7c7ff7 100%);
      box-shadow: 0 5px 24px rgba(99, 102, 241, 0.45);
      transform: scale(1.06);
    }

    .control-btn.skip-btn { background: transparent; border-color: transparent; }
    .control-btn.skip-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.06); }

    .control-btn.download-btn {
      background: rgba(16, 185, 129, 0.15);
      color: #34d399;
      border: 1px solid rgba(16, 185, 129, 0.25);
      padding: 8px 14px;
      font-size: 12px;
    }
    .control-btn.download-btn:hover {
      background: rgba(16, 185, 129, 0.25);
      border-color: rgba(16, 185, 129, 0.4);
      color: #6ee7b7;
    }

    /* Time Display */
    .time-display {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 8px 14px;
      background: rgba(0, 0, 0, 0.35);
      border-radius: 8px;
      font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.3px;
    }
    .time-current { color: #fff; }
    .time-separator { color: rgba(255, 255, 255, 0.25); margin: 0 2px; }
    .time-total { color: rgba(255, 255, 255, 0.45); }

    /* Volume Control */
    .volume-control {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px 4px 4px;
      background: rgba(255, 255, 255, 0.04);
      border-radius: 10px;
      transition: all 0.2s;
    }
    .volume-control:hover { background: rgba(255, 255, 255, 0.08); }

    .volume-slider {
      width: 80px;
      height: 4px;
      -webkit-appearance: none;
      appearance: none;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 100px;
      cursor: pointer;
    }
    .volume-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 14px;
      height: 14px;
      background: #fff;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      transition: transform 0.15s;
    }
    .volume-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
    .volume-slider::-moz-range-thumb {
      width: 14px;
      height: 14px;
      background: #fff;
      border-radius: 50%;
      cursor: pointer;
      border: none;
    }

    /* Speed Select */
    .speed-select {
      padding: 8px 30px 8px 12px;
      background: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='rgba(255,255,255,0.5)'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 10px center;
    }
    .speed-select:hover { background-color: rgba(255, 255, 255, 0.1); }
    .speed-select:focus { outline: none; box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.4); }

    /* Metadata */
    .player-metadata {
      padding: 12px 24px;
      background: rgba(0, 0, 0, 0.2);
      border-top: 1px solid rgba(255, 255, 255, 0.04);
      flex-shrink: 0;
    }
    .fullscreen .player-metadata { display: none; }

    .metadata-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      align-items: center;
    }
    .metadata-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .meta-icon {
      color: rgba(255, 255, 255, 0.3);
      flex-shrink: 0;
    }
    .meta-value {
      color: rgba(255, 255, 255, 0.65);
      font-size: 13px;
      font-weight: 500;
    }

    /* Mobile */
    @media (max-width: 768px) {
      .recording-player-overlay { padding: 0; }
      .recording-player-modal {
        max-height: 100vh;
        max-width: 100%;
        border-radius: 0;
        height: 100vh;
      }
      .player-video-container { max-height: 50vh; }
      .player-header { padding: 14px 18px; }
      .title-badge { padding: 6px 10px; font-size: 10px; }
      .player-title h2 { font-size: 15px; }
      .close-btn { width: 38px; height: 38px; }
      .player-controls { padding: 16px 18px 20px; }
      .controls-row { flex-direction: column; gap: 14px; }
      .controls-left { order: 1; justify-content: center; }
      .controls-right { order: 2; justify-content: center; flex-wrap: wrap; }
      .time-display { order: 0; width: 100%; justify-content: center; margin-bottom: 4px; }
      .control-btn.play-btn { width: 52px; height: 52px; }
      .volume-control { display: none; }
      .progress-bar { height: 7px; }
      .progress-bar:hover { height: 7px; }
      .progress-handle { width: 22px; height: 22px; transform: translateX(-50%) translateY(-50%) scale(1); opacity: 1; }
      .player-metadata { padding: 10px 18px; }
      .metadata-grid { gap: 12px; }
      .download-btn span { display: none; }
    }

    /* Landscape mobile */
    @media (max-height: 500px) {
      .player-video-container { max-height: 50vh; }
      .player-metadata { display: none; }
      .player-header { padding: 10px 18px; }
      .player-controls { padding: 12px 18px; }
      .progress-container { margin-bottom: 12px; }
      .controls-row { flex-direction: row; }
      .time-display { width: auto; order: 0; }
    }
  `}</style>
);

export default RecordingPlayer;
